import { LanguageCode } from '../config/i18n';
import { normalizeLanguageCode } from '../config/normalizeLanguageCode';
import { apiService } from './apiService';

/**
 * Service for managing language preferences with backend synchronization
 */
class LanguageService {
    private readonly STORAGE_KEY = 'preferredLanguage';

    /**
     * Get the current language preference from localStorage
     */
    getLocalLanguage(): LanguageCode | null {
        const stored = localStorage.getItem(this.STORAGE_KEY);
        return normalizeLanguageCode(stored);
    }

    /**
     * Save language preference to localStorage
     */
    setLocalLanguage(language: LanguageCode): void {
        localStorage.setItem(this.STORAGE_KEY, language);
    }

    /**
     * Sync language preference to backend (no-op if logged out)
     */
    async syncToBackend(language: LanguageCode): Promise<void> {
        if (!localStorage.getItem('token')) return;

        try {
            await apiService.post('/update-profile', {
                preferredLanguage: language,
            });
            // Garde userData aligné pour éviter qu'un bootstrap cache
            // réapplique preferred_language: fr au prochain chargement.
            try {
                const raw = localStorage.getItem('userData');
                if (raw) {
                    const userData = JSON.parse(raw) as Record<string, unknown>;
                    userData.preferred_language = language;
                    localStorage.setItem('userData', JSON.stringify(userData));
                }
            } catch {
                /* ignore */
            }
        } catch (error) {
            console.error('Failed to sync language to backend:', error);
            // Local change must still succeed
        }
    }

    /**
     * Fetch language preference from backend (does not write localStorage).
     */
    async fetchFromBackend(): Promise<LanguageCode | null> {
        if (!localStorage.getItem('token')) return null;

        try {
            const response = await apiService.get<{ preferred_language?: string }>(
                '/get-profile'
            );
            return normalizeLanguageCode(response.data?.preferred_language);
        } catch (error) {
            console.error('Failed to fetch language from backend:', error);
            return null;
        }
    }

    /**
     * Initialize language preference.
     * Prefer localStorage if the user changed language while the request was in flight,
     * or if local already differs from a stale server DEFAULT (fr).
     */
    async initializeLanguage(): Promise<LanguageCode | null> {
        const localBefore = this.getLocalLanguage();
        const backendLang = await this.fetchFromBackend();
        const localAfter = this.getLocalLanguage();

        // Changement pendant le fetch : ne pas écraser
        if (localAfter && localBefore && localAfter !== localBefore) {
            return localAfter;
        }

        if (localAfter && backendLang && localAfter !== backendLang) {
            void this.syncToBackend(localAfter);
            return localAfter;
        }

        if (backendLang) {
            this.setLocalLanguage(backendLang);
            return backendLang;
        }

        if (localAfter && localStorage.getItem('token')) {
            void this.syncToBackend(localAfter);
        }
        return localAfter;
    }

    /**
     * Update language preference everywhere
     */
    async updateLanguage(language: LanguageCode): Promise<void> {
        this.setLocalLanguage(language);
        await this.syncToBackend(language);
    }
}

export const languageService = new LanguageService();
